import { UploadOutlined } from '@ant-design/icons';
import {
  Button,
  type ButtonProps,
  Tooltip,
  Upload,
  type UploadProps,
} from 'antd';
import clx from 'classnames';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledUploadButton = styled(Button)`
  &.ant-btn {
    &.multi-line-button {
      height: auto;
      white-space: normal;
      min-height: 32px;
      &.ant-btn-lg {
        min-height: 40px;
      }

      > span {
        display: inline;
      }
    }
  }
`;

const StyledUpload = styled(Upload)`
  width: 100%;

  .ant-upload.ant-upload-select {
    width: 100%;
    padding-bottom: 2px;
  }

  .ant-upload-list {
    .ant-upload-list-item {
      .ant-upload-list-item-actions .ant-upload-list-item-action {
        opacity: 1;
      }
    }
  }

  // quando si omette il nome del file si mostrano le immagini in linea
  &.multi-image-inline-no-name .ant-upload-list.ant-upload-list-picture {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: 0 -8px;
    .ant-upload-list-item-container {
      .ant-upload-list-item-name {
        padding: 0 4px;
      }
    }
  }
`;

export interface UploadInputProps extends Omit<UploadProps, 'children'> {
  /**
   * Permette di sovrascrivere il messaggio principale.
   * @default 'Carica file'
   */
  title?: React.ReactNode;
  /**
   * Permette di personalizzare il pulsante di Upload.
   */
  buttonProps?: Omit<ButtonProps, 'children'>;
  /**
   * Perette di mostrare le immagini caricate in linea quando si omette il nome.
   */
  multiImageInlineNoName?: boolean;
  /**
   * Testo del tooltip. Se non è presente il tooltip non viene mostrato.
   */
  tooltip?: string;
  /**
   * Permette di avere un pulsante du upload con il testo su più righe.
   */
  multiLineButton?: boolean;
}

export type UploadOptions = Parameters<
  Exclude<UploadInputProps['customRequest'], undefined>
>[0];

/**
 * Componente di upload per caricare un file.
 *
 * @param title Permette di sovrascrivere il testo del pulsante.
 */
export function UploadInput(props: UploadInputProps) {
  const {
    title,
    buttonProps,
    disabled,
    className,
    multiImageInlineNoName,
    tooltip,
    multiLineButton,
    ...uploadProps
  } = props;
  const { t } = useTranslation();

  return (
    <StyledUpload
      disabled={disabled}
      {...uploadProps}
      className={clx(className, {
        'multi-image-inline-no-name': multiImageInlineNoName,
      })}
    >
      <Tooltip title={tooltip}>
        <StyledUploadButton
          icon={
            <UploadOutlined
              aria-label={t('inputs.upload-input.aria', 'Carica')}
            />
          }
          disabled={disabled}
          size="large"
          block
          {...buttonProps}
          className={clx(buttonProps?.className, {
            'multi-line-button': multiLineButton,
          })}
        >
          {title ?? t('inputs.upload-input.title', 'Carica file')}
        </StyledUploadButton>
      </Tooltip>
    </StyledUpload>
  );
}
